import { Trash2 } from 'lucide-react';
import type { ToolLookupFilter } from '../../lib/api';
import { LookupFilterValueControl } from './LookupFilterValueControl';
import {
  defaultLookupFilterValue,
  lookupFilterValueMatchesField,
  type LookupSchemaField,
} from './lookup-tool-model';
import styles from './LookupTool.module.css';

interface LookupFilterRowProps {
  filter: ToolLookupFilter;
  fields: LookupSchemaField[];
  /** Field paths any row has selected; the row's own field is exempted in the disabled check. */
  selectedFieldPaths: Set<string>;
  onReplace: (nextFilter: ToolLookupFilter) => void;
  onRemove: () => void;
}

/** One fixed equality filter: field select, value control, and removal. */
export const LookupFilterRow = ({
  filter,
  fields,
  selectedFieldPaths,
  onReplace,
  onRemove,
}: LookupFilterRowProps) => {
  const selectedField = fields.find(field => field.path === filter.field);
  const valueIsValid = selectedField && lookupFilterValueMatchesField(selectedField, filter.value);

  return (
    <li className={styles.filterRow}>
      <select
        aria-label="Filter field"
        value={selectedField ? filter.field : ''}
        onChange={event => {
          const nextField = fields.find(field => field.path === event.target.value);
          if (!nextField) return;
          onReplace({ field: nextField.path, value: defaultLookupFilterValue(nextField) });
        }}>
        {!selectedField && <option value="">{filter.field} — no longer in the schema</option>}
        {fields.map(field => (
          <option
            key={field.path}
            value={field.path}
            disabled={field.path !== filter.field && selectedFieldPaths.has(field.path)}>
            {field.title} ({field.path})
          </option>
        ))}
      </select>
      <span>equals</span>
      {selectedField ? (
        <div className={styles.filterValue}>
          <LookupFilterValueControl
            field={selectedField}
            value={filter.value}
            onChange={value => onReplace({ ...filter, value })}
          />
          {!valueIsValid && (
            <small>The saved value no longer matches this field. Enter a new value.</small>
          )}
        </div>
      ) : (
        <span className={styles.filterFieldMissing}>
          Choose a replacement field, or remove this filter.
        </span>
      )}
      <button type="button" onClick={onRemove} aria-label={`Remove ${filter.field} filter`}>
        <Trash2 />
      </button>
    </li>
  );
};
